"use client";

import { useCallback } from "react";

import { IMPORTED_SESSION_KEYS, IMPORTED_SESSION_KEYS_CLEARED } from "@/constants";
import { useAnalytics } from "@/contexts/analytics-context";
import { useLocalStorage } from "@/hooks/storage";
import { Logger } from "@/utils/logger";

// One-time migration: clear imported session keys saved with the old schema.
// Runs on module load before any hooks read the data.
if (typeof window !== "undefined") {
  if (!localStorage.getItem(IMPORTED_SESSION_KEYS_CLEARED)) {
    localStorage.removeItem(IMPORTED_SESSION_KEYS);
    localStorage.setItem(IMPORTED_SESSION_KEYS_CLEARED, "1");
  }
}

export interface SavedSessionKey {
  privateKey: string;
  subaccountAddress: string;
  ownerAddress?: string;
  accountAddress: string;
  label: string | null;
}

const useImportedSessionKeysLs = () =>
  useLocalStorage<Record<string, string>>(IMPORTED_SESSION_KEYS, {});

const extractSessionKeyFromStorageValue = (value: string) => {
  if (!value) return null;
  try {
    const { privateKey, subaccountAddress, accountAddress, ownerAddress, label } = JSON.parse(
      value,
    ) as SavedSessionKey;
    return { privateKey, subaccountAddress, accountAddress, ownerAddress, label };
  } catch (error) {
    Logger.error(error, {
      message: "Failed to parse imported session key",
      value,
    });
    return null;
  }
};

export const useGetImportedSessionKeys = () => {
  const [importedSessionKeys] = useImportedSessionKeysLs();

  const importedKeys: SavedSessionKey[] = [];

  for (const sessionKey of Object.values(importedSessionKeys)) {
    const key = extractSessionKeyFromStorageValue(sessionKey);
    if (key) {
      importedKeys.push(key);
    }
  }

  return importedKeys;
};

export const useGetImportedSessionKey = (subaccountAddress: string | undefined) => {
  const [importedSessionKeys] = useImportedSessionKeysLs();

  if (Object.keys(importedSessionKeys).length === 0) return null;

  if (subaccountAddress && importedSessionKeys[subaccountAddress]) {
    return extractSessionKeyFromStorageValue(importedSessionKeys[subaccountAddress]);
  }

  const valueAt0thIndex = Object.values(importedSessionKeys)[0];

  if (!valueAt0thIndex) return null;

  return extractSessionKeyFromStorageValue(valueAt0thIndex);
};

export type WithDeploymentPackage<T> = T & {
  deploymentPackage: string;
};

export const useRemoveSessionKey = () => {
  const [, setImportedSessionKeys] = useImportedSessionKeysLs();

  return (subaccountAddress: string) => {
    setImportedSessionKeys((prev) => {
      const newPrev = { ...prev };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newPrev[subaccountAddress];
      return newPrev;
    });
  };
};

export const parseSharedKey = (
  stringifiedJson: string,
): { sharedKey: SavedSessionKey; deploymentPackage: string } | null => {
  const sharedKey = JSON.parse(stringifiedJson) as WithDeploymentPackage<SavedSessionKey>;

  const privateKey = sharedKey.privateKey;
  const subaccountAddress = sharedKey.subaccountAddress;
  const ownerAddress = sharedKey.ownerAddress;
  const accountAddress = sharedKey.accountAddress;
  const label = sharedKey.label;

  if (!privateKey || !subaccountAddress || !accountAddress) {
    return null;
  }

  return {
    sharedKey: {
      privateKey,
      subaccountAddress,
      accountAddress,
      ownerAddress,
      label,
    },
    deploymentPackage: sharedKey.deploymentPackage,
  };
};

// @Todo: Encode the private and subaccount address as a JWT and use that in params instead of the raw values
export const useImportSessionKey = () => {
  const Analytics = useAnalytics();
  const [, setImportedSessionKeys] = useImportedSessionKeysLs();

  const importSessionKey = useCallback(
    ({ privateKey, subaccountAddress, accountAddress, ownerAddress, label }: SavedSessionKey) => {
      if (privateKey && subaccountAddress && accountAddress) {
        Analytics.userBehavior.sessionKeyImported({
          keyName: label || undefined,
          source: "url",
        });
        setImportedSessionKeys((prev) => {
          const newPrev = { ...prev };
          newPrev[subaccountAddress] = JSON.stringify({
            privateKey,
            subaccountAddress,
            accountAddress,
            ownerAddress,
            label,
          });
          return newPrev;
        });
      }
    },
    [setImportedSessionKeys, Analytics],
  );

  return importSessionKey;
};
